import { useMemo } from 'react'
import { useGetAdminsUserId, getGetAdminsUserIdQueryKey } from '@/api/generated/admin/admin'
import { useGetProvidersUserId, getGetProvidersUserIdQueryKey } from '@/api/generated/doctor/doctor'
import type { AdminApiOutContract, DoctorAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { userManagementTypes } from '../Constants'

/**
 * Reads a single clinic member (admin or provider) by id.
 *
 * Replaces the `getMember` thunk + GET_MEMBER* flow for the member-detail page (the
 * `state.member.members[id]` storage). The right GET hook runs based on `userType`; the
 * other is disabled via `enabled`.
 *
 * The old `GET_MEMBER_SUCCESS` reducer case merged `extraData: { UserType: type }` onto the
 * response (neither the admin nor the provider contract returns `UserType`). The page relies
 * on `member.UserType` throughout, so that synthesized field is reproduced here verbatim.
 */
export type MemberData =
  | (AdminApiOutContract & { UserType: string })
  | (DoctorAdminApiOutContract & { UserType: string })

export function useMember(id: string, userType: string) {
  const isAdmin = userType === userManagementTypes.ADMIN
  const isProvider = userType === userManagementTypes.PROVIDER

  const adminQuery = useGetAdminsUserId(id, {
    query: { enabled: !!id && isAdmin },
  })
  const providerQuery = useGetProvidersUserId(id, {
    query: { enabled: !!id && isProvider },
  })

  const activeQuery = isAdmin ? adminQuery : providerQuery

  const member = useMemo<MemberData | undefined>(() => {
    if (!activeQuery.data) return undefined
    return { ...activeQuery.data, UserType: userType }
  }, [activeQuery.data, userType])

  const queryKey = isAdmin ? getGetAdminsUserIdQueryKey(id) : getGetProvidersUserIdQueryKey(id)

  return {
    member,
    queryKey,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    isError: activeQuery.isError,
    error: activeQuery.error,
  }
}
